/**
 * Dependency-light word-level diff for statute amendment rendering (ZMIANY 3/5).
 *
 * Produces a flat token stream {kept | added | removed} from an old/new article
 * text, so the diff page can render the previous wording struck-through and the
 * new wording highlighted. Whitespace is preserved as tokens so spacing survives,
 * but two whitespace runs always compare EQUAL: the snapshots come from PDF
 * re-parses whose line wrapping and spacing drift between runs (" \n" vs "\n",
 * double vs single space), and rendering that drift painted a red/green mark
 * between nearly every word pair. Where a kept whitespace run differs between
 * the sides, the NEW side's run is emitted, so the rendered text always carries
 * the current formatting. Consequence: reconstruction of the OLD text from
 * kept+removed tokens is exact only up to whitespace.
 *
 * Performance: a nowelizacja typically changes a few words inside a long article,
 * so we trim the common prefix/suffix first (O(n)) and only run the O(n*m) LCS on
 * the small differing middle. If the differing middle is still large on both
 * sides (a near-total rewrite), we fall back to a coarse "remove old / add new"
 * block instead of a quadratic blow-up - keeping a 20k-char diff well under 200ms.
 */

export type DiffTokenType = 'kept' | 'added' | 'removed';

export interface DiffToken {
  type: DiffTokenType;
  value: string;
}

// Above this product of (oldMiddle x newMiddle) token counts we stop doing
// word-level LCS and emit a block replacement. 250k cells is a few ms in V8;
// it only triggers on near-total rewrites where word-level detail is noise.
const MAX_LCS_CELLS = 250_000;

/**
 * Split text into tokens: runs of non-whitespace (words) and runs of whitespace,
 * preserved in order. Empty string -> no tokens.
 */
export function tokenize(text: string): string[] {
  if (!text) return [];
  return text.match(/\s+|\S+/g) ?? [];
}

/**
 * Comparison key of a token: every whitespace run collapses to one space, so
 * formatting drift between parser runs never registers as a change.
 */
function tokenKey(token: string): string {
  return /^\s/.test(token) ? ' ' : token;
}

/** Merge adjacent tokens of the same diff type into single tokens. */
function coalesce(tokens: DiffToken[]): DiffToken[] {
  const out: DiffToken[] = [];
  for (const tok of tokens) {
    if (tok.value === '') continue;
    const last = out[out.length - 1];
    if (last && last.type === tok.type) {
      last.value += tok.value;
    } else {
      out.push({ type: tok.type, value: tok.value });
    }
  }
  return out;
}

/**
 * Word-level LCS diff of two token arrays -> ordered removed/kept/added tokens.
 * Classic DP; only ever called on the (small) differing middle. Tokens compare
 * by their `tokenKey`; a kept token carries the NEW side's value.
 */
function lcsDiff(a: string[], b: string[]): DiffToken[] {
  const n = a.length;
  const m = b.length;
  const ak = a.map(tokenKey);
  const bk = b.map(tokenKey);
  // dp[i][j] = LCS length of a[i:] and b[j:].
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] =
        ak[i] === bk[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: DiffToken[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (ak[i] === bk[j]) {
      out.push({ type: 'kept', value: b[j] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ type: 'removed', value: a[i] });
      i++;
    } else {
      out.push({ type: 'added', value: b[j] });
      j++;
    }
  }
  while (i < n) out.push({ type: 'removed', value: a[i++] });
  while (j < m) out.push({ type: 'added', value: b[j++] });
  return out;
}

/**
 * Diff two article texts into a word-level token stream. Either side may be null
 * (added article -> all `added`; removed article -> all `removed`).
 */
export function diffWords(
  oldText: string | null | undefined,
  newText: string | null | undefined,
): DiffToken[] {
  const oldTokens = tokenize(oldText ?? '');
  const newTokens = tokenize(newText ?? '');

  if (oldTokens.length === 0 && newTokens.length === 0) return [];
  if (oldTokens.length === 0) {
    return coalesce(newTokens.map((v) => ({ type: 'added' as const, value: v })));
  }
  if (newTokens.length === 0) {
    return coalesce(oldTokens.map((v) => ({ type: 'removed' as const, value: v })));
  }

  const result: DiffToken[] = [];

  // Common prefix (whitespace-insensitive; the NEW side's value is kept).
  let start = 0;
  const maxStart = Math.min(oldTokens.length, newTokens.length);
  while (
    start < maxStart &&
    tokenKey(oldTokens[start]) === tokenKey(newTokens[start])
  ) {
    result.push({ type: 'kept', value: newTokens[start] });
    start++;
  }

  // Common suffix (not overlapping the prefix).
  let endOld = oldTokens.length - 1;
  let endNew = newTokens.length - 1;
  const suffix: DiffToken[] = [];
  while (
    endOld >= start &&
    endNew >= start &&
    tokenKey(oldTokens[endOld]) === tokenKey(newTokens[endNew])
  ) {
    suffix.push({ type: 'kept', value: newTokens[endNew] });
    endOld--;
    endNew--;
  }
  suffix.reverse();

  const midOld = oldTokens.slice(start, endOld + 1);
  const midNew = newTokens.slice(start, endNew + 1);

  if (midOld.length === 0 && midNew.length === 0) {
    // Only prefix/suffix - texts are identical.
    return coalesce([...result, ...suffix]);
  }

  if (midOld.length * midNew.length > MAX_LCS_CELLS) {
    // Near-total rewrite: coarse block replace instead of quadratic LCS.
    result.push(...midOld.map((v) => ({ type: 'removed' as const, value: v })));
    result.push(...midNew.map((v) => ({ type: 'added' as const, value: v })));
  } else {
    result.push(...lcsDiff(midOld, midNew));
  }

  result.push(...suffix);
  return coalesce(result);
}

/** True if the diff contains any added or removed content (a real change). */
export function hasVisibleChange(tokens: DiffToken[]): boolean {
  return tokens.some((t) => t.type !== 'kept');
}
